import { useState, useRef, useCallback } from 'react'
import { useWebSocketContext } from '@/contexts/WebSocketContext'
import { useTasks } from '@/contexts/TasksContext'
import { getAuthToken, apiFetch } from '@/lib/api'

export default function useKagamiBridge() {
    const [task, setTask] = useState(null)
    const [taskStatus, setTaskStatus] = useState('')
    const [taskState, setTaskState] = useState('')
    const unsubscribeRef = useRef(null)
    const { addListener } = useWebSocketContext()
    const { refresh: refreshTaskBadge } = useTasks()

    const getToken = () => {
        return getAuthToken()
    }

    const cleanup = useCallback(() => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current()
            unsubscribeRef.current = null
        }
    }, [])
    
    const getTask = async (taskId) => {
        const res = await apiFetch(`/api/tasks/${taskId}`)
    
        if(!res.ok){
            throw new Error('Failed to get task')
        }

        const task = await res.json()
    
        setTask(task)
        setTaskStatus(task.status)
        setTaskState(task.state)

        return task
    }
    
    const runTask = async (data = {}) => {
        const token = getToken()
        if (!token) {
            throw new Error('Auth cookie not found')
        }

        // Let the server generate the taskId (MongoDB ObjectId)
        delete data.taskId

        setTaskStatus('Submitting task')

        const res = await apiFetch('/api/tasks/schedule', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        })
    
        if(!res.ok){
            throw new Error('Failed to run task')
        }

        const task = await res.json()

        setTask(task)
        setTaskStatus(task.status || 'pending')
        setTaskState(task.state)

        // Refresh the sidebar active-tasks badge as soon as a task is submitted.
        refreshTaskBadge?.()
    
        return task
    }

    const cancelTask = async (taskId) => {
        const res = await apiFetch(`/api/tasks/${taskId}/cancel`, {
            method: 'POST',
        })

        if (!res.ok) {
            throw new Error('Failed to cancel task')
        }

        return true
    }

    const deleteTask = async (taskId) => {
        const res = await apiFetch(`/api/tasks/${taskId}`, {
            method: 'DELETE',
        })

        if (!res.ok) {
            throw new Error('Failed to delete task')
        }

        return true
    }
    
    const runTaskAndPoll = async (data) => {
        cleanup()

        const submittedTask = await runTask(data)

        const TASK_EVENTS = ['task_updated', 'task_completed', 'task_failed']

        return new Promise((resolve, reject) => {
            unsubscribeRef.current = addListener((message) => {
                if (!TASK_EVENTS.includes(message?.event)) return
                if (message.data?.taskId !== submittedTask._id) return

                const update = message.data
                setTaskStatus(update.status)
                setTaskState(update.state)
                setTask(prev => ({ ...prev, ...update }))

                if (message.event === 'task_completed' || update.state === 'completed') {
                    cleanup()
                    setTaskStatus('')
                    resolve({ ...submittedTask, ...update })
                } else if (message.event === 'task_failed' || update.state === 'failed') {
                    cleanup()
                    reject(new Error(`${update.state}: ${update.status}`))
                }
            })
        })
    }

    return {
        task,
        taskStatus,
        taskState,
        getTask,
        runTask,
        runTaskAndPoll,
        cancelTask,
        deleteTask
    }
}
